/**
 * Guarda "solo la respuesta más reciente" para fetches con carrera (gate de
 * #135): dos requests simultáneos pueden resolverse en orden inverso y la
 * respuesta VIEJA sobrescribiría el estado actual. Cada intento toma un token
 * con begin(); antes de mutar estado se verifica isCurrent(token); cambiar de
 * scope o aplicar una mutación local llama invalidate() para descartar
 * cualquier respuesta en vuelo. Puro y sin React para poder testearlo.
 */
export interface LatestOnly {
  /** Registra un intento nuevo y devuelve su token (invalida los previos). */
  begin(): number;
  /** ¿Este token sigue siendo el intento más reciente? */
  isCurrent(token: number): boolean;
  /** Descarta cualquier intento en vuelo (cambio de scope, mutación local). */
  invalidate(): void;
}

export function makeLatestOnly(): LatestOnly {
  let current = 0;
  return {
    begin: () => {
      current += 1;
      return current;
    },
    isCurrent: (token: number) => token === current,
    invalidate: () => {
      current += 1;
    },
  };
}

/**
 * Variante con IDENTIDAD DE SCOPE (4º re-gate de #135): el guard simple
 * protege loads entre sí, pero un save del scope A que resuelve tarde podía
 * invalidar el load del scope B o pintar la ceremonia de A bajo B. Cada
 * intento captura (scope, seq):
 * - un LOAD aplica solo si sigue siendo el intento más reciente DEL scope
 *   vigente (isCurrent);
 * - un SAVE aplica solo si su scope sigue vigente (isCurrentScope) — y si el
 *   scope cambió, se DESCARTA por completo sin invalidar nada del scope
 *   nuevo;
 * - enterScope() invalida todo lo anterior al cambiar de división/torneo.
 */
export interface ScopedToken {
  scope: string;
  seq: number;
}

export interface ScopedLatestOnly {
  /** Declara el scope vigente (torneo+división) e invalida TODO lo previo. */
  enterScope(scope: string): void;
  /** Registra un intento en el scope vigente (invalida intentos previos). */
  begin(): ScopedToken;
  /** ¿Token del scope vigente Y el intento más reciente? (loads) */
  isCurrent(token: ScopedToken): boolean;
  /** ¿Token del scope vigente? (saves: aplican aunque haya intentos nuevos,
   * pero JAMÁS si el scope cambió) */
  isCurrentScope(token: ScopedToken): boolean;
  /** INVALIDACIÓN TOTAL (5º gate de #135): mata loads Y saves en vuelo —
   * tras unmount una respuesta tardía no puede mutar nada, ni siquiera por
   * la vía isCurrentScope. */
  invalidate(): void;
}

export function makeScopedLatestOnly(): ScopedLatestOnly {
  // El scope interno lleva ÉPOCA (`division-3#7`): volver A→B→A abre una
  // entrada NUEVA de A — un save de la primera visita a A jamás aplica en la
  // segunda aunque el nombre del scope coincida.
  let scopeEpoch = 0;
  let currentScopeId = "";
  let seq = 0;
  return {
    enterScope: (scope: string) => {
      scopeEpoch += 1;
      currentScopeId = `${scope}#${scopeEpoch}`;
      seq += 1;
    },
    begin: () => {
      seq += 1;
      return { scope: currentScopeId, seq };
    },
    isCurrent: (token: ScopedToken) => token.scope === currentScopeId && token.seq === seq,
    isCurrentScope: (token: ScopedToken) => token.scope === currentScopeId,
    invalidate: () => {
      // TOTAL: el scope vigente pasa a un identificador muerto que ningún
      // token pudo capturar (\u0000 no aparece en scopes reales) — falla
      // isCurrent E isCurrentScope. El falso verde del 5º gate era invalidar
      // solo seq: un save pendiente seguía "vigente" tras el unmount.
      scopeEpoch += 1;
      currentScopeId = `\u0000dead#${scopeEpoch}`;
      seq += 1;
    },
  };
}
